import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import { BarChart3, CalendarDays, Check, Lock, Trash2, EyeOff, Clock } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const optionLabel = (o, kind, locale) => {
  if (kind === 'date' && o.date) {
    return `${format(parseISO(o.date), 'EEE, d. MMM yyyy', { locale })}${o.time ? ` · ${o.time}` : ''}`;
  }
  return o.label;
};

const PollOption = ({ poll, option, locale, max, busy, onToggle }) => {
  const { t } = useTranslation();
  const selected = poll.my_votes.includes(option.id);
  const pct = poll.total_voters ? Math.round((option.count / poll.total_voters) * 100) : 0;
  const leading = option.count > 0 && option.count === max;
  return (
    <button
      type="button"
      disabled={poll.closed || busy}
      onClick={(e) => { e.stopPropagation(); onToggle(option.id); }}
      data-testid={`poll-option-${option.id}`}
      className={`relative w-full text-left rounded-xl border overflow-hidden transition-colors ${selected ? 'border-[#D93025] bg-[#FEF2F2]' : 'border-zinc-200 bg-white hover:border-zinc-300'} disabled:cursor-default`}
    >
      <div className={`absolute inset-y-0 left-0 ${leading ? 'bg-[#D93025]/15' : 'bg-zinc-100'} transition-[width] duration-500`} style={{ width: `${pct}%` }} />
      <div className="relative flex items-center gap-2.5 px-3 py-2.5">
        <span className={`shrink-0 w-5 h-5 flex items-center justify-center border ${poll.multiple ? 'rounded-md' : 'rounded-full'} ${selected ? 'bg-[#D93025] border-[#D93025] text-white' : 'border-zinc-300'}`}>
          {selected && <Check className="w-3.5 h-3.5" />}
        </span>
        <span className="flex-1 min-w-0 text-sm font-medium text-zinc-800 [overflow-wrap:anywhere]">{optionLabel(option, poll.kind, locale)}</span>
        <span className="shrink-0 text-xs font-semibold text-zinc-600" data-testid={`poll-option-count-${option.id}`}>{option.count} · {pct}%</span>
      </div>
      {!poll.anonymous && option.voters.length > 0 && (
        <div className="relative px-3 pb-2 -mt-1 pl-10 text-[11px] text-zinc-500 truncate" title={option.voters.map((v) => v.name).join(', ')}>
          {option.voters.map((v) => v.name.split(' ')[0]).join(', ')}
        </div>
      )}
      <span className="sr-only">{selected ? t('polls.yourVote') : ''}</span>
    </button>
  );
};

export const PollCard = ({ poll, onChange, onDeleted }) => {
  const { t, i18n } = useTranslation();
  const [busy, setBusy] = useState(false);
  const locale = i18n.language === 'de' ? de : enUS;
  const max = Math.max(0, ...poll.options.map((o) => o.count));

  const call = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) { toast.error(e.response?.data?.detail || t('polls.error')); } finally { setBusy(false); }
  };

  const toggle = (oid) => call(async () => {
    const mine = poll.my_votes;
    const next = poll.multiple
      ? (mine.includes(oid) ? mine.filter((x) => x !== oid) : [...mine, oid])
      : (mine.includes(oid) ? [] : [oid]);
    const res = await axios.post(`${API_URL}/polls/${poll.id}/vote`, { option_ids: next });
    onChange?.(res.data);
  });

  const close = (e) => { e.stopPropagation(); call(async () => {
    const res = await axios.post(`${API_URL}/polls/${poll.id}/close`);
    onChange?.(res.data);
    toast.success(t('polls.closedToast'));
  }); };

  const remove = (e) => {
    e.stopPropagation();
    if (!window.confirm(t('polls.confirmDelete'))) return;
    call(async () => {
      await axios.delete(`${API_URL}/polls/${poll.id}`);
      toast.success(t('polls.deletedToast'));
      onDeleted?.(poll.id);
    });
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-2xl p-4 text-zinc-900 shadow-sm w-full" data-testid={`poll-card-${poll.id}`}>
      <div className="flex items-start gap-3 mb-3">
        <div className="shrink-0 w-9 h-9 rounded-xl bg-[#FEE2E2] text-[#D93025] flex items-center justify-center">
          {poll.kind === 'date' ? <CalendarDays className="w-[18px] h-[18px]" /> : <BarChart3 className="w-[18px] h-[18px]" />}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-sm sm:text-base leading-snug [overflow-wrap:anywhere]" data-testid={`poll-title-${poll.id}`}>{poll.title}</h3>
          <p className="text-[11px] text-zinc-500 mt-0.5">
            {poll.created_by_name} · {poll.multiple ? t('polls.multiple') : t('polls.single')}
          </p>
        </div>
        {poll.closed && (
          <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide bg-zinc-900 text-white rounded-full px-2 py-1" data-testid={`poll-closed-badge-${poll.id}`}>
            <Lock className="w-3 h-3" />{t('polls.closed')}
          </span>
        )}
      </div>
      {poll.description && <p className="text-sm text-zinc-600 mb-3 whitespace-pre-wrap [overflow-wrap:anywhere]">{poll.description}</p>}
      <div className="space-y-2">
        {poll.options.map((o) => (
          <PollOption key={o.id} poll={poll} option={o} locale={locale} max={max} busy={busy} onToggle={toggle} />
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-3 text-[11px] text-zinc-500">
        <span data-testid={`poll-total-${poll.id}`}>{t('polls.voters', { count: poll.total_voters })}</span>
        {poll.anonymous && <span className="inline-flex items-center gap-1"><EyeOff className="w-3 h-3" />{t('polls.anonymous')}</span>}
        {poll.deadline && (
          <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{t('polls.until')} {format(parseISO(poll.deadline), 'd. MMM, HH:mm', { locale })}</span>
        )}
        {poll.can_manage && (
          <span className="ml-auto flex items-center gap-1">
            {!poll.closed && (
              <button type="button" onClick={close} disabled={busy} data-testid={`poll-close-btn-${poll.id}`} className="px-2 py-1 rounded-lg font-semibold text-zinc-700 hover:bg-zinc-100">
                {t('polls.close')}
              </button>
            )}
            <button type="button" onClick={remove} disabled={busy} data-testid={`poll-delete-btn-${poll.id}`} className="p-1.5 rounded-lg text-zinc-400 hover:text-[#D93025] hover:bg-[#FEE2E2]" title={t('polls.delete')}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </span>
        )}
      </div>
    </div>
  );
};

export default PollCard;
